import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  ClockIcon, 
  PlayIcon,
  StopIcon,
  CalendarIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../common/LoadingSpinner';
import EmptyState from '../common/EmptyState';
import Pagination from '../common/Pagination';

const Timesheets = () => {
  const [timesheets, setTimesheets] = useState([]);
  const [activeTimesheet, setActiveTimesheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [notes, setNotes] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [locationPermission, setLocationPermission] = useState('prompt');
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);

  useEffect(() => {
    fetchTimesheets();
    fetchActiveTimesheet();
  }, []);

  // Update current time every second when there's an active timesheet
  useEffect(() => {
    let interval;
    if (activeTimesheet) {
      interval = setInterval(() => {
        setCurrentTime(new Date());
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeTimesheet]);

  const fetchTimesheets = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/timesheets');
      setTimesheets(response.data);
    } catch (err) {
      console.error('Error fetching timesheets:', err);
      setError('Failed to load timesheets');
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveTimesheet = async () => {
    try {
      const response = await axios.get('/api/timesheets/active');
      setActiveTimesheet(response.data);
    } catch (err) {
      console.error('Error fetching active timesheet:', err);
      // If no active timesheet, response.data will be null
      setActiveTimesheet(null);
    }
  };

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser.'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          };
          setCurrentLocation(location);
          setLocationError(null);
          resolve(location);
        },
        (error) => {
          let errorMessage = 'Unable to retrieve your location.';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location permission denied. Please enable location services.';
              setLocationPermission('denied');
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out.';
              break;
            default:
              errorMessage = 'An unknown error occurred while getting location.';
              break;
          }
          setLocationError(errorMessage);
          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        }
      );
    });
  };

  const handleClockIn = async () => {
    if (isProcessing) return; // Prevent double-clicking
    
    try {
      setIsProcessing(true);
      let locationData = {};
      
      // Try to get location if geolocation is supported
      if (navigator.geolocation) {
        try {
          const location = await getCurrentLocation();
          locationData = {
            latitude: location.latitude,
            longitude: location.longitude
          };
        } catch (locationErr) {
          console.warn('Location not available, proceeding without location:', locationErr.message);
          // Continue without location - it's optional
        }
      }

      await axios.post('/api/timesheets/clock-in', { 
        notes,
        ...locationData
      });
      
      setSuccessMessage('Successfully clocked in!' + (locationData.latitude ? ' (Location captured)' : ''));
      setTimeout(() => setSuccessMessage(''), 3000);
      setNotes('');
      fetchActiveTimesheet();
      fetchTimesheets();
    } catch (err) {
      console.error('Error clocking in:', err);
      alert(err.response?.data?.message || 'Failed to clock in');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClockOut = async () => {
    if (isProcessing) return; // Prevent double-clicking
    
    try {
      setIsProcessing(true);
      let locationData = {};
      
      // Try to get location if geolocation is supported
      if (navigator.geolocation) {
        try {
          const location = await getCurrentLocation();
          locationData = {
            latitude: location.latitude,
            longitude: location.longitude
          };
        } catch (locationErr) {
          console.warn('Location not available, proceeding without location:', locationErr.message);
          // Continue without location - it's optional
        }
      }

      await axios.post('/api/timesheets/clock-out', { 
        notes,
        ...locationData
      });
      
      setSuccessMessage('Successfully clocked out!' + (locationData.latitude ? ' (Location captured)' : ''));
      setTimeout(() => setSuccessMessage(''), 3000);
      setNotes('');
      fetchActiveTimesheet();
      fetchTimesheets();
    } catch (err) {
      console.error('Error clocking out:', err);
      alert(err.response?.data?.message || 'Failed to clock out');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString();
  };


  const getTotalHours = () => {
    if (!activeTimesheet) return 0;
    const clockIn = new Date(activeTimesheet.clockIn);
    const hours = (currentTime - clockIn) / (1000 * 60 * 60);
    return hours.toFixed(2);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        icon={ClockIcon}
        title="Error Loading Timesheets"
        description={error}
        action={{
          label: 'Retry',
          onClick: fetchTimesheets
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Timesheets</h1>
            <p className="text-gray-600 mt-1">Track your work hours</p>
          </div>
        </div>
      </div>

      {/* Clock In/Out Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Time Tracking</h2>
        
        {activeTimesheet ? (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-800">Currently Clocked In</p>
                  <p className="text-sm text-blue-600">
                    Started: {formatDateTime(activeTimesheet.clockIn)}
                  </p>
                                     <p className="text-sm text-blue-600">
                     Current Session: {getTotalHours()} hours
                   </p>
                   <p className="text-sm text-blue-600">
                     Current Time: {currentTime.toLocaleTimeString()}
                   </p>
                </div>
                <div className="flex items-center space-x-2">
                  <ClockIcon className="h-8 w-8 text-blue-500" />
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes (optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={isProcessing}
                className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isProcessing ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
                rows="2"
                placeholder="Add any notes about your work session..."
              />
            </div>

            {/* Location Status */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">Location Services</p>
                  <p className="text-xs text-gray-500">
                    {navigator.geolocation ? 
                      (locationError ? 'Location unavailable' : 'Location will be captured') :
                      'Geolocation not supported'
                    }
                  </p>
                </div>
                <div className="flex items-center">
                  {navigator.geolocation ? (
                    locationError ? (
                      <div className="h-3 w-3 bg-red-400 rounded-full"></div>
                    ) : (
                      <div className="h-3 w-3 bg-green-400 rounded-full"></div>
                    )
                  ) : (
                    <div className="h-3 w-3 bg-gray-400 rounded-full"></div>
                  )}
                </div>
              </div>
            </div>
            
            <button
              onClick={handleClockOut}
              disabled={isProcessing}
              className={`w-full py-3 px-4 rounded-lg font-medium flex items-center justify-center ${
                isProcessing 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-red-600 hover:bg-red-700'
              } text-white`}
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Processing...
                </>
              ) : (
                <>
                  <StopIcon className="h-5 w-5 mr-2" />
                  Clock Out
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-800">Not Currently Clocked In</p>
                  <p className="text-sm text-gray-600">
                    Click the button below to start tracking your time
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <ClockIcon className="h-8 w-8 text-gray-400" />
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes (optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={isProcessing}
                className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isProcessing ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
                rows="2"
                placeholder="Add any notes about your work session..."
              />
            </div>

            {/* Location Status */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">Location Services</p>
                  <p className="text-xs text-gray-500">
                    {navigator.geolocation ? 
                      (locationError ? 'Location unavailable' : 'Location will be captured') :
                      'Geolocation not supported'
                    }
                  </p>
                </div>
                <div className="flex items-center">
                  {navigator.geolocation ? (
                    locationError ? (
                      <div className="h-3 w-3 bg-red-400 rounded-full"></div>
                    ) : (
                      <div className="h-3 w-3 bg-green-400 rounded-full"></div>
                    )
                  ) : (
                    <div className="h-3 w-3 bg-gray-400 rounded-full"></div>
                  )}
                </div>
              </div>
            </div>
            
            <button
              onClick={handleClockIn}
              disabled={isProcessing}
              className={`w-full py-3 px-4 rounded-lg font-medium flex items-center justify-center ${
                isProcessing 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-green-600 hover:bg-green-700'
              } text-white`}
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Processing...
                </>
              ) : (
                <>
                  <PlayIcon className="h-5 w-5 mr-2" />
                  Clock In
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Summary Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <ClockIcon className="h-8 w-8 text-blue-500 mr-3" />
              <div>
                <p className="text-sm font-medium text-blue-800">Total Sessions</p>
                <p className="text-2xl font-bold text-blue-900">{timesheets.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <CalendarIcon className="h-8 w-8 text-green-500 mr-3" />
              <div>
                <p className="text-sm font-medium text-green-800">Total Hours</p>
                <p className="text-2xl font-bold text-green-900">
                  {timesheets
                    .filter(t => t.totalHours)
                    .reduce((sum, t) => sum + t.totalHours, 0)
                    .toFixed(2)}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center">
              <UserIcon className="h-8 w-8 text-purple-500 mr-3" />
              <div>
                <p className="text-sm font-medium text-purple-800">This Week</p>
                <p className="text-2xl font-bold text-purple-900">
                  {timesheets
                    .filter(t => {
                      const weekAgo = new Date();
                      weekAgo.setDate(weekAgo.getDate() - 7);
                      return new Date(t.clockIn) >= weekAgo && t.totalHours;
                    })
                    .reduce((sum, t) => sum + t.totalHours, 0)
                    .toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Timesheet History */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            Timesheet History ({timesheets.length})
          </h2>
        </div>
        
        {timesheets.length === 0 ? (
          <div className="p-6">
            <EmptyState
              icon={ClockIcon}
              title="No Timesheets Found"
              description="Your timesheet history will appear here once you start tracking your time."
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Clock In Date / Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Clock Out Date / Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Hours
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Notes
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(() => {
                  // Apply pagination
                  const startIndex = (currentPage - 1) * itemsPerPage;
                  const endIndex = startIndex + itemsPerPage;
                  const paginatedTimesheets = timesheets.slice(startIndex, endIndex);
                  
                  return paginatedTimesheets.map((timesheet) => (
                  <tr key={timesheet.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {timesheet.employee ? 
                        `${timesheet.employee.firstName} ${timesheet.employee.lastName}` : 
                        'Unknown Employee'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDateTime(timesheet.clockIn)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {timesheet.clockOut ? (
                        formatDateTime(timesheet.clockOut)
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {timesheet.totalHours ? 
                        `${timesheet.totalHours} hours` : 
                        '—'
                      }
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                      <div className="truncate" title={timesheet.notes}>
                        {timesheet.notes || '—'}
                      </div>
                    </td>
                  </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Pagination */}
        {timesheets.length > itemsPerPage && (
          <div className="flex justify-center mt-8">
            <Pagination
              currentPage={currentPage}
              totalItems={timesheets.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Timesheets; 